import { ModeloRetribucionOptions, ColumnaIngresoEmpresaBoolean } from '../types/DropdownMapping';
import { formatArcaNumber } from './formatArcaNumbers';
import { PAYFIT_PAYROLL_NAME } from '../constants/PayfitConstants';
import { PayfitUserData } from '../api/thirdparty/payfit/getPayfitFileData';

/**
 * Type definition for a Payfit row structure
 */
export interface PayfitRowData {
	contractId: string;
	fullName: string;
	nif: string;
	activateEmployee: ColumnaIngresoEmpresaBoolean;
	type: ModeloRetribucionOptions;
	monthlyAmount: string;
	employerMonthlyAmount: string;
	payrollName: string;
}

/**
 * Generates a Payfit file row with the new format
 * Column order: Identificador | Empleado | NIF o NIE | Activar empleado | Tipo | Importe mensual | Importe mensual pagado por empresa | Nombre en nómina
 *
 * @param contractId - Employee contract ID
 * @param fullName - Employee full name
 * @param nif - Employee NIF or NIE
 * @param modeloRetribucion - Compensation model (Modelo mixto or Beneficio en especie)
 * @param monthlyAmount - Total monthly amount (formatted with comma)
 * @param employerMonthlyAmount - Employer monthly contribution (formatted with comma)
 * @returns Array representing a Payfit file row
 */
export function generatePayfitRow(
	contractId: string,
	fullName: string,
	nif: string,
	modeloRetribucion: ModeloRetribucionOptions,
	monthlyAmount: string,
	employerMonthlyAmount: string
): any[] {
	return [
		contractId, // Column 0: Identificador (no modificar)
		fullName, // Column 1: Empleado
		nif, // Column 2: NIF o NIE
		ColumnaIngresoEmpresaBoolean.Si, // Column 3: Activar empleado
		modeloRetribucion, // Column 4: Tipo (Modelo mixto or Beneficio en especie)
		monthlyAmount, // Column 5: Importe mensual
		employerMonthlyAmount, // Column 6: Importe mensual pagado por empresa
		PAYFIT_PAYROLL_NAME // Column 7: Nombre en nómina
	];
}

/**
 * Fills a Payfit file row with data from backend when NIF matches
 *
 * @param row - Original row from uploaded file
 * @param payfitFileData - Array of payfit user data from backend
 * @returns Updated row with filled data, or null if no match found or row is invalid
 */
export function fillPayfitRowData(row: any[], payfitFileData: PayfitUserData[]): any[] | null {
	// Validate row has minimum required columns (need at least 3 for NIF lookup)
	if (row.length < 3) {
		return null;
	}

	const nifIndex = 2; // NIF o NIE is in column 2
	const nif = row[nifIndex];

	// Find matching payfit data by NIF
	const payfitData = payfitFileData.find((data) => data.dni === nif);

	if (!payfitData) {
		return null;
	}

	// Calculate total monthly amount
	const totalAmount =
		Number(payfitData.nextParticipantContribution) + Number(payfitData.nextPromoterContribution);
	const formattedTotalAmount = formatArcaNumber(totalAmount);
	const formattedEmployerAmount = formatArcaNumber(Number(payfitData.nextPromoterContribution));

	// Determine modelo de retribución based on participant contribution
	const modeloRetribucion =
		payfitData.nextParticipantContribution > 0
			? ModeloRetribucionOptions.ModeloMixto
			: ModeloRetribucionOptions.BeneficioEnEspecie;

	// Create new row preserving original data in columns 0, 1, 2
	const newRow = [...row];
	newRow[3] = ColumnaIngresoEmpresaBoolean.Si; // Activar empleado
	newRow[4] = modeloRetribucion; // Tipo (Modelo mixto or Beneficio en especie)
	newRow[5] = formattedTotalAmount; // Importe mensual
	newRow[6] = formattedEmployerAmount; // Importe mensual pagado por empresa
	newRow[7] = PAYFIT_PAYROLL_NAME; // Nombre en nómina

	return newRow;
}
